import React, { useEffect, useState, useMemo } from "react";
import { api, formatCurrency, formatDate, formatApiErrorDetail } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { Plus, MagnifyingGlass, PencilSimple, Trash, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";
import FileAttachment from "../components/app/FileAttachment";

const EMPTY = {
  categoria: "Altro", importo: 0, data: new Date().toISOString().slice(0, 10),
  fornitore: "", descrizione: "", deducibile: true, iva_percent: 22, allegato_id: null, note: "",
};

export default function Spese() {
  const { settings } = useSettings();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("tutte");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/spese");
      setItems(data);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function openNew() { setForm({ ...EMPTY, categoria: settings?.categorie_spese?.[0] || "Altro" }); setEditing("new"); }
  function openEdit(it) { setForm({ ...EMPTY, ...it, data: (it.data || "").slice(0, 10) }); setEditing(it); }
  function close() { setEditing(null); }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, importo: Number(form.importo), iva_percent: Number(form.iva_percent || 0) };
      if (editing === "new") {
        await api.post("/spese", payload);
        toast.success("Spesa registrata");
      } else {
        await api.put(`/spese/${editing.id}`, payload);
        toast.success("Spesa aggiornata");
      }
      close();
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally { setSaving(false); }
  }

  async function remove(it) {
    if (!window.confirm(`Eliminare spesa di ${formatCurrency(it.importo)}?`)) return;
    try {
      await api.delete(`/spese/${it.id}`);
      toast.success("Eliminata");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    }
  }

  const totale = useMemo(() => items.reduce((s, i) => s + (i.importo || 0), 0), [items]);
  const totaleDeducibile = useMemo(() => items.filter(i => i.deducibile).reduce((s, i) => s + (i.importo || 0), 0), [items]);

  const filtered = items.filter((it) => {
    const text = [it.fornitore, it.descrizione, it.categoria].filter(Boolean).join(" ").toLowerCase();
    const matchSearch = text.includes(search.toLowerCase());
    const matchCat = filterCat === "tutte" || it.categoria === filterCat;
    return matchSearch && matchCat;
  });

  const categorie = settings?.categorie_spese || [];

  return (
    <div className="p-4 md:p-12 max-w-[1400px] mx-auto">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-3">Uscite</div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter leading-none">Spese</h1>
          <p className="text-sm text-[#64748B] mt-3">{items.length} registrate</p>
        </div>
        <button onClick={openNew} data-testid="new-spesa-btn" className="btn-primary px-5 py-3 text-xs uppercase tracking-[0.15em] font-bold rounded-sm flex items-center gap-2 self-start">
          <Plus size={14} weight="bold" /> Nuova Spesa
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="swiss-card p-6 bg-[#0A0A0A] text-white border-[#0A0A0A]">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/70">Totale Spese</div>
          <div className="font-display text-3xl font-black tracking-tighter mt-2">{formatCurrency(totale)}</div>
        </div>
        <div className="swiss-card p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B]">Deducibili</div>
          <div className="font-display text-3xl font-black tracking-tighter mt-2">{formatCurrency(totaleDeducibile)}</div>
        </div>
      </div>

      <div className="swiss-card">
        <div className="p-4 border-b border-[#E2E8F0] flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <input placeholder="Cerca fornitore, descrizione..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="spesa-search" className="w-full bg-white border border-[#E2E8F0] rounded-sm pl-10 pr-3 py-2 text-sm focus:border-[#0033A0] outline-none" />
          </div>
          <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} className="bg-white border border-[#E2E8F0] rounded-sm px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold">
            <option value="tutte">Tutte categorie</option>
            {categorie.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-[#64748B]">Caricamento…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Receipt size={32} className="mx-auto text-[#64748B] mb-3" />
            <p className="text-sm text-[#64748B]">{items.length === 0 ? "Nessuna spesa registrata." : "Nessun risultato."}</p>
          </div>
        ) : (
          <>
            {/* Tabella desktop */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F5F7]">
                  <tr className="text-left">
                    <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Data</th>
                    <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Categoria</th>
                    <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Fornitore</th>
                    <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Descrizione</th>
                    <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Deduc.</th>
                    <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold text-right">Importo</th>
                    <th className="py-3 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((it) => (
                    <tr key={it.id} className="border-t border-[#E2E8F0] hover:bg-[#F4F5F7]" data-testid={`spesa-row-${it.id}`}>
                      <td className="py-3 px-4 font-mono text-xs">{formatDate(it.data)}</td>
                      <td className="py-3 px-4"><span className="px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] font-bold bg-gray-100 text-gray-800 border border-gray-300 rounded-sm">{it.categoria}</span></td>
                      <td className="py-3 px-4 font-semibold">{it.fornitore || "—"}</td>
                      <td className="py-3 px-4 text-[#64748B] max-w-xs truncate">{it.descrizione || "—"}</td>
                      <td className="py-3 px-4 text-[#64748B] text-xs">{it.deducibile ? "Sì" : "No"}</td>
                      <td className="py-3 px-4 text-right font-mono font-bold">{formatCurrency(it.importo)}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="inline-flex gap-1">
                          <button onClick={() => openEdit(it)} className="p-1.5 text-[#64748B] hover:text-[#0033A0]" data-testid={`edit-spesa-${it.id}`}><PencilSimple size={16} /></button>
                          <button onClick={() => remove(it)} className="p-1.5 text-[#64748B] hover:text-[#D32F2F]" data-testid={`delete-spesa-${it.id}`}><Trash size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Card stack mobile */}
            <div className="md:hidden divide-y divide-[#E2E8F0]">
              {filtered.map((it) => (
                <div key={it.id} className="px-4 py-4" data-testid={`spesa-card-${it.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] font-bold bg-gray-100 text-gray-800 border border-gray-300 rounded-sm">{it.categoria}</span>
                        <span className="font-mono text-[10px] text-[#64748B]">{formatDate(it.data)}</span>
                        {it.deducibile && <span className="text-[9px] uppercase tracking-[0.15em] font-bold text-[#008A3C]">Deduc.</span>}
                      </div>
                      <div className="font-semibold text-sm truncate">{it.fornitore || it.descrizione || "—"}</div>
                      {it.fornitore && it.descrizione && <div className="text-[11px] text-[#64748B] truncate mt-0.5">{it.descrizione}</div>}
                    </div>
                    <div className="font-mono font-bold text-sm flex-shrink-0">{formatCurrency(it.importo)}</div>
                  </div>
                  <div className="flex justify-end gap-1 mt-2">
                    <button onClick={() => openEdit(it)} className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#0033A0] px-2 py-1" data-testid={`edit-spesa-mobile-${it.id}`}>Modifica</button>
                    <button onClick={() => remove(it)} className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#D32F2F] px-2 py-1 -mr-2" data-testid={`delete-spesa-mobile-${it.id}`}>Elimina</button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={close}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white border border-[#E2E8F0] rounded-sm max-w-2xl w-full max-h-[90vh] overflow-auto thin-scroll" data-testid="spesa-modal">
            <div className="p-6 border-b border-[#E2E8F0] sticky top-0 bg-white">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-2">{editing === "new" ? "Nuova" : "Modifica"}</div>
              <h2 className="font-display text-2xl font-bold">Spesa</h2>
            </div>
            <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Categoria *">
                <select required value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} className="inp" data-testid="spesa-categoria">
                  {categorie.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Data *"><input type="date" required value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} className="inp" /></Field>
              <Field label="Importo *"><input type="number" min={0} step="0.01" required value={form.importo} onChange={(e) => setForm({ ...form, importo: e.target.value })} className="inp font-mono" data-testid="spesa-importo" /></Field>
              <Field label="IVA %"><input type="number" min={0} max={100} step="0.5" value={form.iva_percent} onChange={(e) => setForm({ ...form, iva_percent: e.target.value })} className="inp font-mono" /></Field>
              <Field label="Fornitore" full><input value={form.fornitore || ""} onChange={(e) => setForm({ ...form, fornitore: e.target.value })} className="inp" data-testid="spesa-fornitore" /></Field>
              <Field label="Descrizione" full><input value={form.descrizione || ""} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} className="inp" /></Field>
              <Field label="Note" full><textarea rows={2} value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} className="inp" /></Field>
              <div className="sm:col-span-2 flex items-center gap-2">
                <input type="checkbox" id="deducibile" checked={form.deducibile} onChange={(e) => setForm({ ...form, deducibile: e.target.checked })} className="accent-[#0033A0]" data-testid="spesa-deducibile" />
                <label htmlFor="deducibile" className="text-sm">Spesa fiscalmente <strong>deducibile</strong></label>
              </div>
              <div className="sm:col-span-2">
                <FileAttachment value={form.allegato_id} onChange={(id) => setForm({ ...form, allegato_id: id })} label="Allegato (fattura)" testid="spesa-allegato" />
              </div>
            </div>
            <div className="p-6 border-t border-[#E2E8F0] flex justify-end gap-3 sticky bottom-0 bg-white">
              <button type="button" onClick={close} className="px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold border border-[#E2E8F0] rounded-sm hover:bg-[#F4F5F7]">Annulla</button>
              <button type="submit" disabled={saving} className="btn-primary px-5 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm" data-testid="spesa-save-btn">{saving ? "…" : "Salva"}</button>
            </div>
          </form>
        </div>
      )}
      <style>{`.inp { width: 100%; background: #fff; border: 1px solid #E2E8F0; border-radius: 4px; padding: 8px 12px; font-size: 14px; outline: none; } .inp:focus { border-color: #0033A0; box-shadow: 0 0 0 1px #0033A0; }`}</style>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="block text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5">{label}</label>
      {children}
    </div>
  );
}
